(() => {
  const configNode = document.getElementById('FuntoSceneConfig');
  const main = document.getElementById('MainContent');
  if (!configNode || !main) return;
  const config = JSON.parse(configNode.textContent);
  const instances = new Set();
  const cache = new Map();
  const mobile = matchMedia('(max-width: 749px)');
  const numbers = new URLSearchParams(location.search).get('funto_motion_numbers') === '1' || config.designMode;
  if (numbers) document.documentElement.classList.add('funto-motion-numbers');
  if (getComputedStyle(main).position === 'static') main.style.position = 'relative';
  const layer = document.createElement('div');
  layer.className = 'funto-scene-layer';
  layer.setAttribute('aria-hidden', 'true');
  main.append(layer);
  const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
  const sizes = { seated:150, surfer:72, walker:56, duck:106, gull:92, tree:76, sailboat:100 };
  const resolve = (selector, headingText) => {
    for (const part of selector.split(',')) {
      const candidates = [...document.querySelectorAll(part.trim())].filter(el => !headingText || el.textContent.toLowerCase().replace(/[^a-z]/g,'').includes(headingText));
      const node = candidates.find(el => el.getBoundingClientRect().width > 0 && el.getBoundingClientRect().height > 0);
      if (node) return node;
    }
    return headingText ? null : document.querySelector(selector);
  };
  const marker = (node, id) => {
    node.dataset.funtoMotionId = id;
    if (node.querySelector(':scope > .funto-motion-marker')) return;
    const span = document.createElement('span');
    span.className = 'funto-motion-marker';
    span.setAttribute('aria-hidden', 'true');
    span.textContent = id;
    node.append(span);
  };
  const loadAtlas = url => {
    if (!cache.has(url)) cache.set(url, new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = url;
    }));
    return cache.get(url);
  };
  let geometryRequest = null;
  const scheduleGeometry = () => {
    if (geometryRequest !== null) return;
    geometryRequest = requestAnimationFrame(() => {
      geometryRequest = null;
      instances.forEach(instance => instance.measure());
    });
  };
  const boundsFor = target => {
    if (target.matches('.article-template__content,.funto-magazine__body')) return target.getBoundingClientRect();
    const candidates = [...target.querySelectorAll('.page-width,.page-width-desktop,.funto-magazine__wrap')];
    const candidate = candidates.find(el => el.getBoundingClientRect().width > 0);
    if (candidate) return candidate.getBoundingClientRect();
    const rect = target.getBoundingClientRect();
    const width = Math.min(1400, Math.max(0, innerWidth - 80), rect.width);
    return {left:(innerWidth-width)/2,right:(innerWidth+width)/2,width};
  };
  if (!customElements.get('funto-scene-motion')) customElements.define('funto-scene-motion', class extends HTMLElement {
    connectedCallback() {
      if(this.relocating)return;
      this.sceneSettings = this.sceneSlot;
      this.asset = config.assets[this.sceneSettings?.assetKey || this.dataset.kind];
      if (!this.sceneSettings || !this.asset) return;
      this.art = this.querySelector('.funto-scene__art');
      this.actor = this.querySelector('.funto-scene__actor');
      this.canvas = this.querySelector('canvas');
      this.context = this.canvas.getContext('2d');
      this.elapsed = 0;
      this.distance = 0;
      this.x = null;
      this.y = null;
      this.frame = -1;
      this.last = null;
      this.animation = null;
      this.lastScroll = 0;
      this.active = true;
      this.loading = false;
      this.atlas = null;
      instances.add(this);
      this.measure();
      if ('IntersectionObserver' in window) {
        this.active = false;
        this.observer = new IntersectionObserver(() => this.syncVisibility(), { threshold:0, rootMargin:'32px' });
        this.observer.observe(this);
      } else this.resume();
      this.syncVisibility();
    }
    flowPlace(target) {
      this.dataset.placement = 'flow';
      const adjacent = target.previousElementSibling === this || target.nextElementSibling === this;
      if (!adjacent) {
        this.relocate(()=>{if (this.sceneSettings.anchor === 'bottom') target.after(this);else target.before(this);});
      }
    }
    relocate(move){this.relocating=true;move();this.relocating=false;}
    measure() {
      const target = this.targetNode?.isConnected ? this.targetNode : resolve(this.sceneSettings.target,this.sceneSettings.headingText);
      if (!target) return;
      this.targetNode = target;
      const asset = this.asset;
      const desired = Number(this.sceneSettings.desktopWidth) || sizes[this.dataset.kind] || 90;
      const layout = this.sceneSettings.layout;
      let p01Size = null;
      if (this.sceneSettings.id === 'P01') {
        const fontSize = parseFloat(getComputedStyle(target).fontSize) || 32;
        const visibleRatio = this.sceneSettings.personHeightRatio || 0.667;
        const height = fontSize * 2.2 / visibleRatio;
        p01Size = height * asset.fw / asset.fh;
        target.classList.add('funto-scene-p01-title');
        target.parentElement.classList.add('funto-scene-p01-heading');
        target.parentElement.style.setProperty('--p01-person-height',height+'px');
        target.parentElement.style.setProperty('--p01-person-width',p01Size+'px');
      } else if (this.sceneSettings.titleHeightMultiple || this.sceneSettings.titleWidth) {
        if (!this.titleWrap?.isConnected) {
          const wrap=document.createElement('div');
          wrap.className='funto-scene-sized-title';
          target.before(wrap);
          this.titleWrap=wrap;
          wrap.append(target);
        }
        const style=getComputedStyle(target);
        const lineHeight=parseFloat(style.lineHeight)||(parseFloat(style.fontSize)||28)*1.2;
        const visibleRatio=this.sceneSettings.visibleHeightRatio||1;
        const height=this.sceneSettings.titleWidth ? (mobile.matches ? (this.sceneSettings.mobileTitleWidth||this.sceneSettings.titleWidth) : this.sceneSettings.titleWidth)*asset.fh/asset.fw : lineHeight*this.sceneSettings.titleHeightMultiple/visibleRatio;
        p01Size=height*asset.fw/asset.fh;
        this.titleWrap.style.setProperty('--scene-title-width',p01Size+'px');
        this.titleWrap.style.setProperty('--scene-title-height',height+(this.sceneSettings.titleTravel||0)+'px');
        if(this.sceneSettings.centerTitle)this.titleWrap.style.justifyContent='center';
      }
      let size = desired;
      if (layout === 'viewport') {
        this.dataset.placement = 'viewport';
        if (this.parentElement !== document.body) this.relocate(()=>document.body.append(this));
        this.hidden = mobile.matches;
        if (mobile.matches) { this.pause(); return; }
        const content = boundsFor(main);
        const laneWidth = this.sceneSettings.laneWidth || 140;
        size = Math.min(80,Math.max(24,innerWidth-content.right-22));
        const header = document.querySelector('sticky-header, .shopify-section-header');
        const headerBottom = header ? Math.max(0,header.getBoundingClientRect().bottom) : 0;
        const top = Math.max(innerHeight*0.22,Math.min(innerHeight*0.35,headerBottom+24));
        const inset = Math.min(116,Math.max(12,innerWidth-content.right-size-16));
        this.style.left = innerWidth-laneWidth-inset+'px';
        this.style.top = top+'px';
        this.style.width = laneWidth+'px';
        this.style.height = Math.max(0,innerHeight-top-24)+'px';
        this.style.setProperty('--scene-actor-left',Math.max(0,laneWidth-size-8)+'px');
        const atc = resolve('#MainContent button[name="add"][type="submit"], #MainContent .product-form__submit, #MainContent [id^="ProductSubmitButton-"]');
        const review = resolve('#MainContent #judgeme_product_reviews, #MainContent .jdgm-review-widget') || (this.sceneSettings.reviewTarget ? resolve(this.sceneSettings.reviewTarget) : null);
        this.journeyStart = atc ? atc.getBoundingClientRect().top+scrollY : main.getBoundingClientRect().top+scrollY;
        this.journeyEnd = review ? review.getBoundingClientRect().top+scrollY : main.getBoundingClientRect().bottom+scrollY;
        this.journeyEnd = Math.max(this.journeyStart+innerHeight,this.journeyEnd);
        this.travelLimit = Math.max(0,Math.min(innerHeight*(this.sceneSettings.travelViewport||0.3),innerHeight-top-24-size*asset.fh/asset.fw-12));
      } else if (layout === 'name-side' && !mobile.matches) {
        if (this.parentElement !== layer) this.relocate(()=>layer.append(this));
        this.dataset.placement = 'overlay';
        size = Number(this.sceneSettings.desktopWidth) || 116;
        const rootRect = main.getBoundingClientRect();
        const heading = target.getBoundingClientRect();
        const range = document.createRange();
        range.selectNodeContents(target);
        const text = range.getBoundingClientRect();
        const height = size*asset.fh/asset.fw;
        this.style.left = (Math.min(innerWidth-size-8,text.right+12)-rootRect.left)+'px';
        this.style.top = (heading.top-rootRect.top+(heading.height-height)/2)+'px';
        this.style.width = size+'px';
        this.style.height = height+'px';
      } else if (layout === 'search') {
        target.classList.add('funto-guide-search--scene');
        if (this.parentElement !== target || target.firstElementChild !== this) this.relocate(()=>target.prepend(this));
        this.dataset.placement = 'search';
        size = mobile.matches ? (Number(this.sceneSettings.mobileWidth) || 40) : (Number(this.sceneSettings.desktopWidth) || 48);
        const artHeight = size*asset.fh/asset.fw;
        this.style.width = size+'px';
        this.style.height = artHeight+'px';
        this.style.position = mobile.matches ? 'relative' : 'absolute';
        this.style.margin = mobile.matches ? '0 auto 12px' : '0';
        if (mobile.matches) {
          this.style.left = 'auto';
          this.style.top = 'auto';
        } else {
          const box = target.getBoundingClientRect();
          const input = target.querySelector('[data-guide-search]');
          const field = input ? input.getBoundingClientRect() : box;
          const gap = Math.min(12,Math.max(4,innerWidth-box.right-size-8));
          this.style.left = box.width+gap+'px';
          this.style.top = field.top-box.top+(field.height-artHeight)/2+'px';
        }
      } else if (layout === 'button') {
        if (!this.buttonWrap?.isConnected) {
          const wrap = document.createElement('span');
          wrap.className = 'funto-scene-button-wrap';
          target.before(wrap);
          this.buttonWrap = wrap;
          this.relocate(()=>wrap.append(target, this));
        }
        this.dataset.placement = 'button';
        size = mobile.matches ? (Number(this.sceneSettings.mobileWidth) || 80) : (Number(this.sceneSettings.desktopWidth) || 92);
        if (this.sceneSettings.desktopWidth || this.sceneSettings.mobileWidth) {
          this.style.width = size+'px';
          this.style.height = (size*asset.fh/asset.fw+8)+'px';
        }
      } else if (mobile.matches && (this.sceneSettings.mobileBefore || this.sceneSettings.mobileAfter)) {
        const anchor = resolve(this.sceneSettings.mobileTarget) || target;
        const after = Boolean(this.sceneSettings.mobileAfter);
        if (after ? this.previousElementSibling !== anchor : this.nextElementSibling !== anchor) this.relocate(()=>after ? anchor.after(this) : anchor.before(this));
        this.dataset.placement = after ? 'creator-between' : 'creator-before';
        size = Number(this.sceneSettings.mobileWidth) || 102;
        this.style.removeProperty('left');
        this.style.removeProperty('top');
        this.style.width = after ? '100%' : 'calc(100% - 32px)';
        this.style.height = (size*asset.fh/asset.fw+(after ? 32 : 8))+'px';
      } else if (mobile.matches && this.sceneSettings.mobileAnchor === 'first-two-artworks') {
        if (this.parentElement !== layer) this.relocate(()=>layer.append(this));
        this.dataset.placement = 'gallery-mobile-overlay';
        const cards = [...target.querySelectorAll('.funto-gallery-card, .custom-columns__column')].filter(card=>card.getBoundingClientRect().width>0);
        const card = cards[Math.min(1,cards.length-1)] || target;
        const image = card.querySelector('img');
        const imageRect = image?.getBoundingClientRect();
        const anchor = imageRect?.width > 0 && imageRect?.height > 0 ? imageRect : card.getBoundingClientRect();
        const rootRect = main.getBoundingClientRect();
        size = Number(this.sceneSettings.mobileWidth) || 44;
        const height = size*asset.fh/asset.fw;
        this.mobileGalleryTravel = Math.min(Number(this.sceneSettings.mobileTravel)||32,Math.max(0,anchor.height-height-16));
        this.galleryMobileStartScroll = anchor.top+scrollY-innerHeight*.80;
        this.galleryMobileEndScroll = Math.max(this.galleryMobileStartScroll+1,anchor.bottom+scrollY-innerHeight*.40);
        this.style.left = Math.max(0,anchor.right-rootRect.left-size-8)+'px';
        this.style.top = (anchor.bottom-rootRect.top-height-this.mobileGalleryTravel-10)+'px';
        this.style.width = size+'px';
        this.style.height = (height+this.mobileGalleryTravel)+'px';
      } else if (layout === 'title' && this.sceneSettings.mobileAbove && mobile.matches) {
        target.parentElement.classList.add('funto-scene-title-stack');
        if (this.parentElement !== target.parentElement || this.nextElementSibling !== target) this.relocate(()=>target.before(this));
        this.dataset.placement = 'title-above';
        size = p01Size || 140;
        this.style.removeProperty('left');
        this.style.removeProperty('top');
        this.style.width = size+'px';
        this.style.height = size*asset.fh/asset.fw+8+'px';
      } else if (layout === 'title') {
        if (this.parentElement !== target) this.relocate(()=>target.append(this));
        if (getComputedStyle(target).position === 'static') target.style.position = 'relative';
        this.dataset.placement = 'title';
        const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT, {acceptNode:node => node.parentElement.closest('funto-scene-motion') || !node.textContent.trim() ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT});
        const texts = [];
        while (walker.nextNode()) texts.push(walker.currentNode);
        const r = target.getBoundingClientRect();
        let textRight = r.left;
        if (texts.length) {
          const range = document.createRange();
          range.setStart(texts[0],0);
          range.setEnd(texts[texts.length-1],texts[texts.length-1].length);
          textRight = range.getBoundingClientRect().right;
        }
        const parentRight = target.parentElement.getBoundingClientRect().right;
        size = p01Size || Number(this.sceneSettings.desktopWidth) || Math.min(mobile.matches ? 100 : 132, Math.max(56,parentRight-textRight-20));
        this.style.left = textRight-r.left+12+'px';
        this.style.top = (r.height-size*asset.fh/asset.fw)/2+'px';
        this.style.width = size+'px';
        this.style.height = size*asset.fh/asset.fw+'px';
      } else if (layout === 'cross') {
        this.flowPlace(target);
        size = mobile.matches ? 82 : 106;
      } else if (mobile.matches) {
        this.flowPlace(target);
        size = Number(this.sceneSettings.mobileWidth) || desired * 0.82;
        this.style.removeProperty('left');
        this.style.removeProperty('top');
        this.style.removeProperty('width');
        this.style.removeProperty('height');
        this.style.setProperty('--scene-flow-x',this.sceneSettings.side === 'left' ? '10%' : '70%');
      } else {
        if (this.parentElement !== layer) this.relocate(()=>layer.append(this));
        this.dataset.placement = 'overlay';
        const rootRect = main.getBoundingClientRect();
        const startRect = target.getBoundingClientRect();
        const end = this.sceneSettings.endTarget ? resolve(this.sceneSettings.endTarget) : target;
        const endRect = end ? end.getBoundingClientRect() : startRect;
        const bounds = boundsFor(target);
        const right = this.sceneSettings.side !== 'left';
        const space = right ? innerWidth-bounds.right : bounds.left;
        size = Math.min(desired,Math.max(16,space-16));
        let left = right ? bounds.right+8 : bounds.left-size-8;
        left = clamp(left,6,Math.max(6,innerWidth-size-6));
        const actorHeight = size*asset.fh/asset.fw;
        const vertical = this.sceneSettings.mode === 'vertical';
        let top = startRect.top-rootRect.top+24;
        let height = vertical ? Math.max(actorHeight+20,endRect.bottom-startRect.top-36) : actorHeight+12;
        if (this.sceneSettings.route === 'gallery-rows') {
          const rowBounds = node => {
            const cards = [...node.querySelectorAll('.funto-gallery-card, .custom-columns__column')].map(el=>el.getBoundingClientRect()).filter(r=>r.width>0&&r.height>0);
            return cards.length ? {top:Math.min(...cards.map(r=>r.top)),bottom:Math.max(...cards.map(r=>r.bottom))} : node.getBoundingClientRect();
          };
          const firstRow = rowBounds(target);
          const lastRow = rowBounds(end || target);
          top = firstRow.bottom-rootRect.top-actorHeight-8;
          const finishTop = lastRow.bottom-rootRect.top-actorHeight/2-14;
          this.galleryTravel = Math.max(0,finishTop-top);
          height = this.galleryTravel+actorHeight;
          this.galleryStartScroll = firstRow.top+scrollY-innerHeight*0.60;
          this.galleryEndScroll = Math.max(this.galleryStartScroll+1,lastRow.bottom+scrollY-innerHeight*0.60);
        }
        if (this.sceneSettings.anchor === 'bottom') top = endRect.bottom-rootRect.top-actorHeight-24;
        this.style.left = left-rootRect.left+'px';
        this.style.top = top+'px';
        this.style.width = size+'px';
        this.style.height = height+'px';
      }
      this.style.setProperty('--scene-size',size+'px');
      this.actorHeight = size*asset.fh/asset.fw;
      this.actorWidth = size;
      this.x = null;
      this.y = null;
      this.updatePosition(true);
    }
    syncVisibility() {
      if (!this.isConnected || !this.actor) return;
      const rect = this.actor.getBoundingClientRect();
      this.active = rect.width > 0 && rect.height > 0 && rect.bottom > -32 && rect.top < innerHeight+32 && rect.right > 0 && rect.left < innerWidth && !(this.sceneSettings.desktopOnly && mobile.matches);
      if (this.active && !document.hidden) this.resume();
      else this.pause();
    }
    resume() {
      if (!this.active || document.hidden || (this.sceneSettings.desktopOnly && mobile.matches)) return;
      if(!this.hasAttribute('data-active')) this.setAttribute('data-active','');
      if (!this.loading && this.sceneSettings.frames !== false && this.asset.atlas && this.context) {
        this.loading = true;
        loadAtlas(this.asset.atlas).then(image => {if(this.isConnected){this.atlas=image;this.resume();}}).catch(()=>{});
      }
      if(this.sceneSettings.mode==='fixed' && (this.sceneSettings.frames===false || !this.atlas)) return;
      if (this.animation !== null) return;
      this.last = null;
      this.animation = requestAnimationFrame(t=>this.tick(t));
    }
    scroll(delta) {
      if (!this.active || document.hidden || this.sceneSettings.mode === 'fixed') return;
      this.distance += delta * 0.10;
      this.lastScroll = performance.now();
    }
    updatePosition(immediate=false) {
      const rect = this.getBoundingClientRect();
      let tx=0,ty=0;
      const progress=clamp((innerHeight-rect.top)/(innerHeight+rect.height),0,1);
      if(this.sceneSettings.mode==='viewport'){
        this.journeyProgress=clamp((scrollY+innerHeight*0.55-this.journeyStart)/(this.journeyEnd-this.journeyStart),0,1);
        ty=(1-this.journeyProgress)*(this.travelLimit||0);
      }
      if (this.sceneSettings.mode === 'vertical' && this.sceneSettings.route === 'gallery-rows') {
        if (mobile.matches && this.sceneSettings.mobileAnchor === 'first-two-artworks') {
          const routeProgress = clamp((scrollY-this.galleryMobileStartScroll)/(this.galleryMobileEndScroll-this.galleryMobileStartScroll),0,1);
          ty = routeProgress*(this.mobileGalleryTravel||0);
        } else if (!mobile.matches) {
          const routeProgress = clamp((scrollY-this.galleryStartScroll)/(this.galleryEndScroll-this.galleryStartScroll),0,1);
          ty = routeProgress*(this.galleryTravel||0);
        }
      } else if (this.sceneSettings.mode === 'vertical' && this.sceneSettings.titleTravel) ty=(progress-0.5)*this.sceneSettings.titleTravel;
      else if (this.sceneSettings.mode === 'vertical') ty=progress*Math.max(0,rect.height-this.actorHeight-14);
      if (this.sceneSettings.mode === 'cross') tx=8+(1-progress)*Math.max(0,rect.width-this.actorWidth-16);
      if (immediate || this.x === null) {this.x=tx;this.y=ty;}
      else {this.x+=(tx-this.x)*0.18;this.y+=(ty-this.y)*0.18;}
      this.style.setProperty('--scene-x',this.x.toFixed(2)+'px');
      this.style.setProperty('--scene-y',this.y.toFixed(2)+'px');
      if (this.sceneSettings.footprints) {
        const viewport = this.sceneSettings.mode === 'viewport';
        const walked = viewport ? Math.max(0,(this.travelLimit||0)-this.y) : Math.max(0,this.y);
        const height = Math.min(180,walked);
        this.style.setProperty('--trail-height',height+'px');
        this.style.setProperty('--trail-top',(viewport ? this.y+this.actorHeight+6 : Math.max(0,this.y-height+this.actorHeight*0.78))+'px');
      }
    }
    draw(frame) {
      if (!this.atlas || !this.context) return;
      const {fw,fh}=this.asset;
      if(frame!==this.frame){this.context.clearRect(0,0,fw,fh);this.context.drawImage(this.atlas,frame%5*fw,Math.floor(frame/5)*fh,fw,fh,0,0,fw,fh);this.frame=frame;}
      this.canvas.hidden=false;
      this.art.setAttribute('data-ready','');
    }
    poster() {this.canvas.hidden=true;this.art.removeAttribute('data-ready');}
    tick(time) {
      this.animation=null;
      if(!this.active || document.hidden)return;
      if(this.last!==null)this.elapsed+=Math.min(100,time-this.last);
      this.last=time;
      this.updatePosition();
      if(this.atlas){
        const count=this.asset.count;
        if(this.sceneSettings.route==='gallery-rows' && !mobile.matches){
          const routeProgress=this.galleryTravel>0?clamp(this.y/this.galleryTravel,0,1):0;
          this.draw(Math.floor(routeProgress*count*4)%count);
        }else if(this.sceneSettings.mode==='viewport'){
          const progress=this.travelLimit>0?clamp(1-this.y/this.travelLimit,0,1):0;
          this.draw(Math.floor(progress*count*6)%count);
        }else if(this.sceneSettings.mode!=='fixed'){
          if(time-this.lastScroll<300)this.draw(((Math.floor(this.distance)%count)+count)%count);
        }else{
          const fps=Number(this.sceneSettings.fps)||(this.sceneSettings.animation==='quiet'?3:(this.dataset.kind==='seated'?6:12));
          this.draw(Math.floor(this.elapsed*fps/1000)%count);
        }
      }
      this.animation=requestAnimationFrame(t=>this.tick(t));
    }
    pause(){cancelAnimationFrame(this.animation);this.animation=null;this.last=null;this.removeAttribute('data-active');}
    disconnectedCallback(){if(this.relocating)return;this.pause();this.observer?.disconnect();instances.delete(this);}
  });
  let lastScrollY=scrollY;
  let visibilityRequest = null;
  const refreshVisibility = () => {
    if (visibilityRequest !== null) return;
    visibilityRequest = requestAnimationFrame(() => {
      visibilityRequest = null;
      instances.forEach(i=>i.syncVisibility());
    });
  };
  addEventListener('scroll',()=>{const delta=scrollY-lastScrollY;lastScrollY=scrollY;instances.forEach(i=>i.scroll(delta));refreshVisibility();},{passive:true});
  addEventListener('pageshow',()=>{instances.forEach(i=>i.pause());scheduleGeometry();refreshVisibility();});
  window.visualViewport?.addEventListener('resize',refreshVisibility,{passive:true});
  window.visualViewport?.addEventListener('scroll',refreshVisibility,{passive:true});
  addEventListener('resize',()=>{scheduleGeometry();refreshVisibility();},{passive:true});
  addEventListener('load',scheduleGeometry);
  document.addEventListener('load',event=>{if(event.target.tagName==='IMG')scheduleGeometry();},true);
  document.fonts?.ready.then(scheduleGeometry);
  if('ResizeObserver' in window)new ResizeObserver(scheduleGeometry).observe(main);
  document.addEventListener('visibilitychange',()=>{if(document.hidden)instances.forEach(i=>i.pause());else refreshVisibility();});
  const create=slot=>{
    const asset=config.assets[slot.assetKey || slot.kind];
    if(!asset)return null;
    const node=document.createElement('funto-scene-motion');
    node.sceneSlot=slot;
    node.id='FuntoMotion-'+slot.id;
    node.className='funto-scene';
    node.dataset.kind=slot.kind;
    node.dataset.layout=slot.layout;
    node.dataset.mode=slot.mode;
    node.dataset.animation=slot.animation;
    if(slot.desktopOnly)node.setAttribute('data-desktop-only','');
    if(slot.mobileAbove && slot.animation==='static')node.setAttribute('data-static-title','');
    node.setAttribute('aria-hidden','true');
    node.style.setProperty('--scene-aspect',asset.fw+'/'+asset.fh);
    if(slot.footprints){const trail=document.createElement('span');trail.className='funto-scene__trail';node.style.setProperty('--scene-footprints','url('+JSON.stringify(config.footprints)+')');node.append(trail);}
    const actor=document.createElement('span');actor.className='funto-scene__actor';
    const art=document.createElement('span');art.className='funto-scene__art';
    const image=document.createElement('img');image.src=slot.flock && config.flockBirds?.[0]?.src ? config.flockBirds[0].src : asset.still;image.width=asset.fw;image.height=asset.fh;image.alt='';image.loading='lazy';image.decoding='async';
    const canvas=document.createElement('canvas');canvas.width=asset.fw;canvas.height=asset.fh;canvas.hidden=true;
    art.append(image,canvas);
    if (slot.chairSlide && asset.chair) {
      image.className = 'funto-furniture-table';
      const chair = document.createElement('img');
      chair.className = 'funto-furniture-chair';
      chair.src = asset.chair;
      chair.width = asset.fw;
      chair.height = asset.fh;
      chair.alt = '';
      chair.loading = 'lazy';
      chair.decoding = 'async';
      art.append(chair);
    }
    if(slot.grassTufts && config.grassTufts){
      node.setAttribute('data-grass','');
      image.className='funto-grass-base';
      config.grassTufts.forEach((tuft,index)=>{
        const leaf=document.createElement('img');
        leaf.className='funto-grass-tuft';
        leaf.src=tuft.src;leaf.alt='';leaf.loading='lazy';leaf.decoding='async';
        leaf.style.left=tuft.left+'%';leaf.style.bottom=tuft.bottom+'%';
        leaf.style.setProperty('--tuft-width',tuft.width+'%');
        leaf.style.setProperty('--tuft-duration',[4.2,5.1,4.6][index%3]+'s');
        leaf.style.setProperty('--tuft-phase',[-0.4,-1.7,-2.8][index%3]+'s');
        leaf.style.setProperty('--tuft-bend',[2,3,2.4][index%3]+'deg');
        art.append(leaf);
      });
    }
    actor.append(art);node.append(actor);
    if(slot.flock){
      node.setAttribute('data-flock','');
      const timings=[['4.6s','0s','6px'],['5.5s','-1.4s','4px'],['4.1s','-2.7s','5px'],['6.2s','-3.6s','3px']];
      const count=Math.min(4,Math.max(1,Number(slot.flock)));
      for(let i=0;i<count;i++){
        const bird=i===0?actor:actor.cloneNode(true);
        bird.dataset.bird=String(i);
        const variant=config.flockBirds?.[i];
        if(variant){
          bird.querySelector('.funto-scene__art img').src=variant.src;
          bird.style.setProperty('--bird-angle',variant.angle);
          bird.style.setProperty('--bird-facing',String(variant.flip));
        }
        bird.style.setProperty('--bird-duration',timings[i][0]);
        bird.style.setProperty('--bird-phase',timings[i][1]);
        bird.style.setProperty('--bird-lift',timings[i][2]);
        if(i>0)node.append(bird);
      }
    }
    marker(node,slot.id);
    return node;
  };
  const tagExisting=()=>{
    [['H01','.funto-step-animation funto-brand-motion[data-kind="palette"]'],['H02','.funto-step-animation funto-brand-motion[data-kind="brush"]'],['H03','.funto-step-animation funto-brand-motion[data-kind="book"]'],['H04','.funto-film-home funto-brand-motion[data-kind="van"]'],['H05','.funto-film-home funto-brand-motion[data-kind="cactus"]'],['H06','.funto-home-book > funto-brand-motion'],['H07','.funto-blog-cat > funto-brand-motion']].forEach(([id,selector])=>document.querySelectorAll(selector).forEach(node=>marker(node,id)));
    document.querySelectorAll('funto-gallery-motion').forEach(node=>marker(node,'G04'));
  };
  fetch(config.mapUrl).then(r=>{if(!r.ok)throw Error('Scene map unavailable');return r.json();}).then(map=>{
    const slots=map[config.page]||[];
    const mount=()=>{
      document.querySelectorAll('.funto-scene').forEach(node=>{if(!slots.some(slot=>slot.id===node.dataset.funtoMotionId))node.remove();});
      slots.forEach(slot=>{
        if(document.getElementById('FuntoMotion-'+slot.id))return;
        const target=resolve(slot.target,slot.headingText);
        if(!target)return;
        const node=create(slot);
        if(!node)return;
        if(slot.layout==='cross'||mobile.matches && !['title','button'].includes(slot.layout))target.before(node);
        else layer.append(node);
      });
      tagExisting();scheduleGeometry();
    };
    mount();
    document.addEventListener('shopify:section:load',mount);
  }).catch(tagExisting);
})();










